/* eslint-disable @typescript-eslint/no-floating-promises, @typescript-eslint/no-use-before-define, @typescript-eslint/no-non-null-asserted-optional-chain, no-plusplus, @typescript-eslint/await-thenable */
import { useState, useCallback, useRef, useEffect } from 'react';
import { ImageFit, Pipeline, VirtualBackgroundProcessor } from '@twilio/video-processors';
import { LocalVideoTrack } from 'twilio-video';
import { safeRemoveProcessor, preloadImage } from '../utils/videoEffectUtils';
import { BackgroundImage } from '../videoEffectTypes';
import { VideoEffectStore } from '../store/videoEffectModel';

const DIMENSIONS_TIMEOUT_MS = 3000;

export interface VirtualBackgroundOptions {
  maskBlurRadius?: number;
  fitType?: ImageFit;
  pipeline?: Pipeline;
  debounce?: boolean;
}

export const DEFAULT_VIRTUAL_BACKGROUND_OPTIONS: Required<VirtualBackgroundOptions> = {
  maskBlurRadius: 3,
  fitType: ImageFit.Cover,
  pipeline: Pipeline.Canvas2D,
  debounce: true,
};

function waitForTrackDimensions(track: LocalVideoTrack, timeoutMs = DIMENSIONS_TIMEOUT_MS): Promise<boolean> {
  if (track.dimensions?.width && track.dimensions?.height) {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    const onDimensionsChanged = () => {
      // istanbul ignore next
      if (track.dimensions?.width && track.dimensions?.height) {
        cleanup();
        resolve(true);
      }
    };

    const timeout = setTimeout(() => {
      cleanup();
      resolve(false);
    }, timeoutMs);

    function cleanup() {
      clearTimeout(timeout);
      track.off('dimensionsChanged', onDimensionsChanged);
    }

    track.on('dimensionsChanged', onDimensionsChanged);
  });
}

function bakeImageFit(
  image: HTMLImageElement,
  targetWidth: number,
  targetHeight: number,
  fitType: ImageFit,
): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    let ctx: CanvasRenderingContext2D | null = null;
    try {
      ctx = canvas.getContext('2d');
    } catch {
      ctx = null;
    }

    if (!ctx) {
      reject(new Error('2D canvas context unavailable — cannot emulate fitType.'));
      return;
    }
    // istanbul ignore else
    if (fitType === ImageFit.Fill) {
      ctx.drawImage(image, 0, 0, targetWidth, targetHeight);
    } else {
      const imageRatio = image.naturalWidth / image.naturalHeight;
      const targetRatio = targetWidth / targetHeight;
      let drawWidth = targetWidth;
      let drawHeight = targetHeight;

      if (imageRatio > targetRatio) {
        drawHeight = targetWidth / imageRatio;
      } else {
        drawWidth = targetHeight * imageRatio;
      }

      ctx.fillRect(0, 0, targetWidth, targetHeight);
      ctx.drawImage(image, (targetWidth - drawWidth) / 2, (targetHeight - drawHeight) / 2, drawWidth, drawHeight);
    }

    const baked = new Image();
    baked.onload = () => resolve(baked);
    baked.onerror = /* istanbul ignore next */ () => reject(new Error('Failed to bake fitType image.'));
    baked.src = canvas.toDataURL();
  });
}

export const useVirtualBackground = (assetsPath: string, initialOptions?: VirtualBackgroundOptions) => {
  const [isApplying, setIsApplying] = useState(false);
  const [options, setOptions] = useState<Required<VirtualBackgroundOptions>>({
    ...DEFAULT_VIRTUAL_BACKGROUND_OPTIONS,
    ...initialOptions,
  });
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const isVideoFrameSupported = VideoEffectStore.useStoreState((state) => state.isVideoFrameSupported);
  const setActiveBackgroundId = VideoEffectStore.useStoreActions((state) => state.setActiveBackgroundId);
  const activeBackgroundId = VideoEffectStore.useStoreState((state) => state.activeBackgroundId);
  const videoTrack = VideoEffectStore.useStoreState((state) => state.localVideoTrack);

  const processorRef = useRef<VirtualBackgroundProcessor | null>(null);
  const processorConfigRef = useRef<{ assetsPath: string; pipeline: Pipeline; debounce: boolean } | null>(null);
  const requestIdRef = useRef(0);
  const lastBackgroundRef = useRef<BackgroundImage | null>(null);

  const imageCacheRef = useRef<Map<string, HTMLImageElement>>(new Map());
  const bakedImageCacheRef = useRef<Map<string, HTMLImageElement>>(new Map());

  const removeCurrentProcessor = useCallback(async () => {
    // istanbul ignore next
    if (!videoTrack) return;
    await safeRemoveProcessor(videoTrack, processorRef.current);
    processorRef.current = null;
    processorConfigRef.current = null;
  }, [videoTrack]);

  const resolveImage = useCallback(async (background: BackgroundImage): Promise<HTMLImageElement> => {
    if (background.imageElement?.complete) {
      return background.imageElement;
    }
    const cached = imageCacheRef.current.get(background.id);
    if (cached) return cached;

    const img = await preloadImage(background?.url!);
    imageCacheRef.current.set(background.id, img);
    return img;
  }, []);

  const applyBackground = useCallback(
    async (background: BackgroundImage | null, callOptions?: { force?: boolean } & Partial<VirtualBackgroundOptions>) => {
      if (!videoTrack || !isVideoFrameSupported) return;
      if (isApplying) return;

      const { force, ...optionOverrides } = callOptions ?? {};
      const isTogglingOff = !force && background !== null && background.id === activeBackgroundId;
      const effective: Required<VirtualBackgroundOptions> = { ...optionsRef.current, ...optionOverrides };

      setIsApplying(true);
      const requestId = ++requestIdRef.current;

      try {
        if (!background || isTogglingOff) {
          await removeCurrentProcessor();
          setActiveBackgroundId(null);
          lastBackgroundRef.current = null;
          return;
        }

        const dimensionsReady = await waitForTrackDimensions(videoTrack);
        // istanbul ignore next
        if (requestId !== requestIdRef.current) return;

        if (!dimensionsReady) {
          console.warn('Camera dimensions never initialized; skipping background apply.');
          await removeCurrentProcessor();
          setActiveBackgroundId(null);
          return;
        }

        const imageElement = await resolveImage(background);
        // istanbul ignore next
        if (requestId !== requestIdRef.current) return;

        let finalImage = imageElement;
        const { width, height } = videoTrack.dimensions;

        // Emulate fitting configurations inside the software rendering engine
        if (effective.pipeline === Pipeline.WebGL2 && effective.fitType !== ImageFit.Cover && width && height) {
          const cacheKey = `${background.id}|${effective.fitType}|${width}x${height}`;
          const cachedBaked = bakedImageCacheRef.current.get(cacheKey);
          if (cachedBaked) {
            finalImage = cachedBaked;
          } else {
            try {
              finalImage = await bakeImageFit(imageElement, width, height, effective.fitType);
              bakedImageCacheRef.current.set(cacheKey, finalImage);
            } catch (bakeError) {
              console.warn('Could not emulate fitType; falling back to native handling.', bakeError);
              finalImage = imageElement;
            }
          }
        }
        // istanbul ignore next
        if (requestId !== requestIdRef.current) return;

        const needsNewProcessor =
          !processorRef.current ||
          processorConfigRef.current?.assetsPath !== assetsPath ||
          processorConfigRef.current?.pipeline !== effective.pipeline ||
          processorConfigRef.current?.debounce !== effective.debounce;

        if (needsNewProcessor) {
          await removeCurrentProcessor();

          const processor = new VirtualBackgroundProcessor({
            assetsPath,
            backgroundImage: finalImage,
            fitType: effective.fitType,
            maskBlurRadius: effective.maskBlurRadius,
            pipeline: effective.pipeline,
            debounce: effective.debounce,
          });

          await processor.loadModel();
          // istanbul ignore next
          if (requestId !== requestIdRef.current) return;

          // CONTEXT ALIGNMENT LAYER: Match Twilio framework delivery straight to Canvas choices
          const isCanvas2D = effective.pipeline === Pipeline.Canvas2D;
          await videoTrack.addProcessor(processor, {
            inputFrameBufferType: isCanvas2D ? 'canvas' : 'video',
            outputFrameBufferContextType: isCanvas2D ? '2d' : 'webgl2',
          });

          processorRef.current = processor;
          processorConfigRef.current = { assetsPath, pipeline: effective.pipeline, debounce: effective.debounce };
        } else {
          // In-place live mutations when configurations match up seamlessly
          const processor = processorRef.current!;
          processor.backgroundImage = finalImage;
          processor.fitType = effective.fitType;
          processor.maskBlurRadius = effective.maskBlurRadius;
        }

        lastBackgroundRef.current = background;
        setActiveBackgroundId(background.id);
      } catch (error) {
        console.error('Failed to apply virtual background:', error);
        await removeCurrentProcessor();
        setActiveBackgroundId(null);
        lastBackgroundRef.current = null;
      } finally {
        // istanbul ignore next
        if (requestId === requestIdRef.current) {
          setIsApplying(false);
        }
      }
    },
    [
      assetsPath,
      isApplying,
      isVideoFrameSupported,
      removeCurrentProcessor,
      resolveImage,
      setActiveBackgroundId,
      activeBackgroundId,
      videoTrack,
    ],
  );

  const updateOptions = useCallback(
    (patch: Partial<VirtualBackgroundOptions>) => {
      const prev = optionsRef.current;
      const next = { ...prev, ...patch };
      optionsRef.current = next;
      setOptions(next);

      const processor = processorRef.current;
      const pipelineOrDebounceChanged =
        (patch.pipeline !== undefined && patch.pipeline !== prev.pipeline) ||
        (patch.debounce !== undefined && patch.debounce !== prev.debounce);

      if (pipelineOrDebounceChanged) {
        if (lastBackgroundRef.current) {
          applyBackground(lastBackgroundRef.current, { force: true, ...next });
        }
        return;
      }

      // Hot update existing values without unmounting pipelines
      if (processor) {
        if (patch.fitType !== undefined) processor.fitType = patch.fitType;
        if (patch.maskBlurRadius !== undefined) processor.maskBlurRadius = patch.maskBlurRadius;
      }
    },
    [applyBackground],
  );

  const clearImageCache = useCallback(() => {
    imageCacheRef.current.clear();
    bakedImageCacheRef.current.clear();
  }, []);

  useEffect(() => {
    return () => {
      requestIdRef.current += 1;
      if (videoTrack && processorRef.current) {
        safeRemoveProcessor(videoTrack, processorRef.current);
      }
      processorRef.current = null;
      processorConfigRef.current = null;
    };
  }, [videoTrack]);

  return {
    applyBackground,
    updateOptions,
    options,
    isApplying,
    clearImageCache,
  };
};
